import React from 'react';
import { Link } from 'react-router-dom';
import MemberPhotoSlider from './MemberPhotoSlider';
import { getUserImages } from '../../utils/userImages';
import defaultUserImage from '../../assets/images/user/default.webp';
import watermarkImage from '../../assets/images/watermark.png';
import './ProfileMemberCard.css';

const calculateAge = (dob) => {
  if (!dob) return null;
  const birthDate = new Date(dob);
  if (Number.isNaN(birthDate.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1;
  }
  return age > 0 ? age : null;
};

const formatHeight = (height) => {
  if (!height && height !== 0) return 'N/A';
  const str = String(height).trim().replace(/ft|feet|"/gi, '').trim();
  const parts = str.split('.');
  if (parts.length === 2) {
    return `${parts[0]}'${parts[1]}"`;
  }
  if (!Number.isNaN(Number(str)) && Number(str) > 0 && Number(str) < 10) {
    return `${str}'0"`;
  }
  return str;
};

const formatGender = (gender) => {
  if (!gender) return 'N/A';
  const g = String(gender).toLowerCase();
  if (g === 'male') return 'Male';
  if (g === 'female') return 'Female';
  return gender;
};

const toTitleCase = (value) => {
  if (!value && value !== 0) return '';
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/\b([a-z])/g, (ch) => ch.toUpperCase());
};

/**
 * Standard Hindu Sanjog member card — exact mockup styling across the entire website.
 */
const ProfileMemberCard = ({
  member,
  to,
  onClick,
  nestedSlider = false,
  className = '',
  hideName = false,
}) => {
  if (!member) return null;

  const age = calculateAge(member.dob) || (member.age ? Number(member.age) : null);
  const ageLabel = age != null ? `${age} Yrs` : (member.age ? `${member.age} Yrs` : 'N/A');
  const images = getUserImages(member);
  const primaryImage = images[0] || member.image1 || defaultUserImage;

  const isPremiumMember = Boolean(
    member.is_premium === true ||
    member.is_premium === 1 ||
    member.is_premium === '1'
  );

  const rawId = member.user_id || member.id || member.uid || '';
  const memberId = rawId ? (String(rawId).toUpperCase().startsWith('HS') ? String(rawId).toUpperCase() : `HS${rawId}`) : 'HS';

  const rawName = (member.name || (member.fname ? `${member.fname} ${member.lname || ''}` : '') || member.username || member.full_name || '').trim();
  const displayName = rawName || memberId || 'Member';

  const inner = (
    <>
      <div className="member-photo member-photo--luxury">
        {isPremiumMember && (
          <span className="member-premium-badge" title="Premium Member">
            <span className="member-premium-badge__crown" aria-hidden>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 17h18l-1.5-9-4.5 4L12 5l-3 7-4.5-4L3 17zm2 2h14v2H5v-2z" />
              </svg>
            </span>
            Premium
          </span>
        )}
        {nestedSlider && images.length > 1 ? (
          <MemberPhotoSlider
            images={images}
            alt={displayName}
            imageClassName="member-image member-image--clean"
            nested={nestedSlider}
          />
        ) : (
          <img
            src={primaryImage}
            alt={displayName}
            className="member-image member-image--clean"
            loading="lazy"
            onError={(e) => {
              e.target.src = defaultUserImage;
              e.target.onerror = null;
            }}
          />
        )}
        <div className="member-card-watermark" aria-hidden="true">
          <img src={watermarkImage} alt="" className="member-card-watermark-img" />
        </div>
      </div>
      <div className="member-info member-info--luxury">
        {!hideName && (
          <h5 className="member-name-luxury" title={displayName}>
            {displayName}
          </h5>
        )}
        <div className="member-id-luxury">
          {memberId}
        </div>

        <div className="member-grid-luxury">
          <div className="grid-cell">
            <span className="cell-val">{ageLabel}</span>
            <span className="cell-lbl">Age</span>
          </div>
          <div className="grid-cell">
            <span className="cell-val">{formatHeight(member.height)}</span>
            <span className="cell-lbl">Height</span>
          </div>
          <div className="grid-cell">
            <span className="cell-val">{formatGender(member.gender)}</span>
            <span className="cell-lbl">Gender</span>
          </div>
          <div className="grid-cell">
            <span className="cell-val">{member.caste || 'N/A'}</span>
            <span className="cell-lbl">Caste</span>
          </div>
          <div className="grid-cell">
            <span className="cell-val">{toTitleCase(member.religion) || 'Hindu'}</span>
            <span className="cell-lbl">Religion</span>
          </div>
          <div className="grid-cell">
            <span className="cell-val">{member.curr_location || member.city || member.country || 'India'}</span>
            <span className="cell-lbl">Location</span>
          </div>
        </div>

        <button type="button" className="btn-view-profile-luxury" tabIndex={-1}>
          <span>View Profile</span>
          <svg className="arrow-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </button>
      </div>
    </>
  );

  const classes = `member-card member-card--luxury ${isPremiumMember ? 'member-card--premium' : ''} ${className}`.trim();

  if (to) {
    return (
      <Link to={to} className={classes} onClick={onClick}>
        {inner}
      </Link>
    );
  }

  return (
    <div
      className={classes}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter') onClick(e); } : undefined}
      style={onClick ? { cursor: 'pointer' } : {}}
    >
      {inner}
    </div>
  );
};

export default ProfileMemberCard;
